import React, { useEffect, useState } from 'react';
import { ArrowBack } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';

type Modo = null | 'existente' | 'novo';

const EspeciePendencias = () => {
  const [pendencias, setPendencias] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [modoAberto, setModoAberto] = useState<Record<string, Modo>>({});

  const [buscaDestino, setBuscaDestino] = useState<Record<string, string>>({});
  const [resultadosBusca, setResultadosBusca] = useState<Record<string, any[]>>({});
  const [uidDestinoEscolhido, setUidDestinoEscolhido] = useState<Record<string, string>>({});
  const [criarComoAlias, setCriarComoAlias] = useState<Record<string, boolean>>({});

  const [nomeNovo, setNomeNovo] = useState<Record<string, string>>({});
  const [cientificoNovo, setCientificoNovo] = useState<Record<string, string>>({});

  const [resolvendo, setResolvendo] = useState<Record<string, boolean>>({});
  const [erro, setErro] = useState<Record<string, string>>({});

  const fetchPendencias = () => {
    setCarregando(true);
    api.get('/pesca/especies/pendencias')
      .then((res) => setPendencias(res.data.data))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { fetchPendencias(); }, []);

  const abrirModo = (uid: string, modo: Modo, pendencia: any) => {
    setModoAberto((prev) => ({ ...prev, [uid]: prev[uid] === modo ? null : modo }));
    if (modo === 'novo' && nomeNovo[uid] === undefined) {
      setNomeNovo((prev) => ({ ...prev, [uid]: pendencia.texto_informado }));
    }
  };

  const buscarDestino = (uid: string, texto: string) => {
    setBuscaDestino((prev) => ({ ...prev, [uid]: texto }));
    if (texto.trim().length < 2) {
      setResultadosBusca((prev) => ({ ...prev, [uid]: [] }));
      return;
    }
    api.get(`/pesca/especies?search=${encodeURIComponent(texto.trim())}`)
      .then((res) => setResultadosBusca((prev) => ({ ...prev, [uid]: res.data.data })));
  };

  const removerDaLista = (uid: string) => setPendencias((prev) => prev.filter((p) => p.uid_pesca_especie_pendencia !== uid));

  const resolverComExistente = async (pendencia: any) => {
    const uid = pendencia.uid_pesca_especie_pendencia;
    const uidDestino = uidDestinoEscolhido[uid];
    if (!uidDestino) return;
    setResolvendo((prev) => ({ ...prev, [uid]: true }));
    setErro((prev) => ({ ...prev, [uid]: '' }));
    try {
      await api.post(`/pesca/especies/pendencias/${uid}/resolver`, {
        uid_pesca_especie_resolvida: uidDestino,
        criar_como_alias: !!criarComoAlias[uid],
      });
      removerDaLista(uid);
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [uid]: err?.response?.data?.message || 'Erro ao resolver.' }));
    } finally {
      setResolvendo((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const resolverComNovaEspecie = async (pendencia: any) => {
    const uid = pendencia.uid_pesca_especie_pendencia;
    const nome = nomeNovo[uid]?.trim();
    if (!nome) return;
    setResolvendo((prev) => ({ ...prev, [uid]: true }));
    setErro((prev) => ({ ...prev, [uid]: '' }));
    try {
      const nova = await api.post('/pesca/especies', {
        nome_popular: nome,
        nome_cientifico: cientificoNovo[uid]?.trim() || null,
      });
      await api.post(`/pesca/especies/pendencias/${uid}/resolver`, {
        uid_pesca_especie_resolvida: nova.data.data.uid_pesca_especie,
        // O termo já vira o nome_popular da espécie nova — só cria alias
        // extra se o admin editou o nome exibido de forma diferente.
        criar_como_alias: normalizarSimples(nome) !== normalizarSimples(pendencia.texto_informado),
      });
      removerDaLista(uid);
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [uid]: err?.response?.data?.message || 'Erro ao criar espécie.' }));
    } finally {
      setResolvendo((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const descartar = async (pendencia: any) => {
    const uid = pendencia.uid_pesca_especie_pendencia;
    if (!window.confirm('Descartar essa pendência sem resolver?')) return;
    setResolvendo((prev) => ({ ...prev, [uid]: true }));
    try {
      await api.post(`/pesca/especies/pendencias/${uid}/descartar`, {});
      removerDaLista(uid);
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [uid]: err?.response?.data?.message || 'Erro ao descartar.' }));
      setResolvendo((prev) => ({ ...prev, [uid]: false }));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Pendências de Espécie</h1>
        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{pendencias.length}</span>
          </p>
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-8px', marginBottom: '16px' }}>
        Termos de espécie que pescadores informaram (WhatsApp, importação de ficha) e o sistema ainda não conseguiu
        reconhecer automaticamente. Associe a uma espécie já existente ou cadastre uma nova.
      </p>

      {carregando ? (
        <p style={{ color: '#999' }}>Carregando...</p>
      ) : pendencias.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', textAlign: 'center', color: '#999' }}>
          Nenhuma pendência no momento.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {pendencias.map((p) => {
            const uid = p.uid_pesca_especie_pendencia;
            return (
              <div key={uid} style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>"{p.texto_informado}"</strong>
                    {p.uid_pesca_area && <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#999' }}>região específica</span>}
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '4px' }}>
                      Recebido em {new Date(p.syn_cadmom).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button style={smallBtnStyle(modoAberto[uid] === 'existente')} onClick={() => abrirModo(uid, 'existente', p)}>
                      É espécie existente
                    </button>
                    <button style={smallBtnStyle(modoAberto[uid] === 'novo')} onClick={() => abrirModo(uid, 'novo', p)}>
                      É espécie nova
                    </button>
                    <button style={dangerBtnStyle} disabled={resolvendo[uid]} onClick={() => descartar(p)}>
                      Descartar
                    </button>
                  </div>
                </div>

                {modoAberto[uid] === 'existente' && (
                  <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                    <input
                      style={inputStyle}
                      placeholder="Buscar espécie pelo nome..."
                      value={buscaDestino[uid] || ''}
                      onChange={(e) => buscarDestino(uid, e.target.value)}
                    />
                    {(resultadosBusca[uid] || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                        {resultadosBusca[uid].map((esp: any) => (
                          <button
                            key={esp.uid_pesca_especie}
                            onClick={() => setUidDestinoEscolhido((prev) => ({ ...prev, [uid]: esp.uid_pesca_especie }))}
                            style={chipStyle(uidDestinoEscolhido[uid] === esp.uid_pesca_especie)}
                          >
                            {esp.nome_popular}
                          </button>
                        ))}
                      </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '0.82rem', color: '#555' }}>
                      <input
                        type="checkbox"
                        checked={!!criarComoAlias[uid]}
                        onChange={(e) => setCriarComoAlias((prev) => ({ ...prev, [uid]: e.target.checked }))}
                      />
                      Guardar "{p.texto_informado}" como {p.uid_pesca_area ? 'nome regional' : 'alias'} dessa espécie
                    </label>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                      <button
                        style={primaryBtnStyle}
                        disabled={!uidDestinoEscolhido[uid] || resolvendo[uid]}
                        onClick={() => resolverComExistente(p)}
                      >
                        {resolvendo[uid] ? 'Resolvendo...' : 'Confirmar'}
                      </button>
                    </div>
                  </div>
                )}

                {modoAberto[uid] === 'novo' && (
                  <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 2, minWidth: '160px' }}>
                      <label style={labelStyle}>Nome popular</label>
                      <input
                        style={inputStyle}
                        value={nomeNovo[uid] ?? p.texto_informado}
                        onChange={(e) => setNomeNovo((prev) => ({ ...prev, [uid]: e.target.value }))}
                      />
                    </div>
                    <div style={{ flex: 2, minWidth: '160px' }}>
                      <label style={labelStyle}>Nome científico (opcional)</label>
                      <input
                        style={inputStyle}
                        value={cientificoNovo[uid] ?? ''}
                        onChange={(e) => setCientificoNovo((prev) => ({ ...prev, [uid]: e.target.value }))}
                      />
                    </div>
                    <button style={primaryBtnStyle} disabled={resolvendo[uid]} onClick={() => resolverComNovaEspecie(p)}>
                      {resolvendo[uid] ? 'Criando...' : 'Criar e vincular'}
                    </button>
                  </div>
                )}

                {erro[uid] && <p style={{ color: '#f75a68', fontSize: '0.82rem', marginTop: '8px' }}>{erro[uid]}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function normalizarSimples(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: '4px' };
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
};
const dangerBtnStyle: React.CSSProperties = {
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
  border: '1px solid #f7c8cc', background: '#fff', color: '#f75a68',
};
const smallBtnStyle = (ativo: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
  border: ativo ? '2px solid #03c9fe' : '1px solid #ddd', background: ativo ? '#eaf9ff' : '#fff', color: '#333',
});
const chipStyle = (selecionado: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer',
  border: selecionado ? '2px solid #00b37e' : '1px solid #ddd', background: selecionado ? '#e6fbf3' : '#fafafa', color: '#333',
});

export default EspeciePendencias;
